import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { transaction_type: 'expense', category: '', amount: '', description: '', transaction_date: new Date().toISOString().split('T')[0], crop_id: '', land_id: '', payment_method: 'cash', reference_no: '' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

// Fallback lists — used only if the settings API fails for any reason
// This keeps the form functional even if backend has issues
const FALLBACK_EXPENSE_CATS = ['Labor', 'Fertilizer', 'Pesticide', 'Seeds', 'Irrigation', 'Machinery', 'Fuel', 'Lease/Rent', 'Electricity', 'Transport', 'Other'];
const FALLBACK_INCOME_CATS = ['Harvest Sale', 'Subsidy', 'Crop Insurance', 'Other'];

const FinanceTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [crops, setCrops] = useState([]);
  const [lands, setLands] = useState([]);
  const [expenseCats, setExpenseCats] = useState(FALLBACK_EXPENSE_CATS);
  const [incomeCats, setIncomeCats] = useState(FALLBACK_INCOME_CATS);
  const [catsLoaded, setCatsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/agriculture/finance`, { headers: headers() }),
      axios.get(`${API}/agriculture/crops`, { headers: headers() }),
      axios.get(`${API}/agriculture/lands`, { headers: headers() })
    ]).then(([f, c, l]) => { setTransactions(f.data.data); setCrops(c.data.data); setLands(l.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // Fetch categories from settings API, with safe fallback to hardcoded lists
  const fetchCategories = async () => {
    try {
      const [exp, inc] = await Promise.all([
        axios.get(`${API}/agriculture/settings/expense_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/income_category`, { headers: headers() })
      ]);
      // Use API data only if it returned at least 1 category, otherwise keep fallback
      const expValues = (exp.data.data || []).map(item => item.value);
      const incValues = (inc.data.data || []).map(item => item.value);
      if (expValues.length > 0) setExpenseCats(expValues);
      if (incValues.length > 0) setIncomeCats(incValues);
      setCatsLoaded(true);
    } catch (err) {
      console.warn('Settings API unavailable, using fallback categories', err);
      // Keep the fallback lists already in state — owner can still use the form
      setCatsLoaded(true);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchCategories();
  }, []);

  const openAdd = (type = 'expense') => { setForm({ ...EMPTY, transaction_type: type }); setEditId(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ ...t, transaction_date: t.transaction_date?.split('T')[0] || '' }); setEditId(t.id); setShowForm(true); };

  const handleSave = async () => {
    if (!form.category || !form.amount || !form.transaction_date) return alert('Category, amount and date are required');
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/finance/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/finance`, form, { headers: headers() });
      setShowForm(false); fetchAll();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    await axios.delete(`${API}/agriculture/finance/${id}`, { headers: headers() });
    fetchAll();
  };

  const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
  const totalIncome = transactions.filter(t => t.transaction_type === 'income').reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalExpense = transactions.filter(t => t.transaction_type === 'expense').reduce((s, t) => s + parseFloat(t.amount), 0);
  const filtered = filter === 'all' ? transactions : transactions.filter(t => t.transaction_type === filter);
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(filtered, 10);

  // When editing an existing transaction, the saved category might not be in the current list
  // (e.g., owner deleted it from settings, or it was renamed). Include it in the dropdown so
  // the edit form doesn't show an empty selection for old data.
  const getCategoryOptions = () => {
    const baseList = form.transaction_type === 'income' ? incomeCats : expenseCats;
    if (form.category && !baseList.includes(form.category)) {
      // Add the current value at the top so user can see what's saved
      return [form.category, ...baseList];
    }
    return baseList;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Finance</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => openAdd('income')} style={{ padding: '12px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Income</button>
          <button onClick={() => openAdd('expense')} style={{ padding: '12px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Expense</button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Income', value: fmt(totalIncome), color: '#27ae60', icon: '💰' },
          { label: 'Total Expenses', value: fmt(totalExpense), color: '#e74c3c', icon: '📉' },
          { label: 'Net Profit/Loss', value: fmt(totalIncome - totalExpense), color: totalIncome - totalExpense >= 0 ? '#27ae60' : '#e74c3c', icon: '📊' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${c.color}` }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{c.icon}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['all', 'income', 'expense'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === f ? '#27ae60' : '#f0f0f0', color: filter === f ? 'white' : '#555'
          }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Date', 'Type', 'Category', 'Description', 'Crop', 'Field', 'Amount', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No transactions found.</td></tr>
            ) : paginatedData.map((t, i) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px 16px', color: '#555' }}>{new Date(t.transaction_date).toLocaleDateString('en-PK')}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: t.transaction_type === 'income' ? '#27ae6020' : '#e74c3c20', color: t.transaction_type === 'income' ? '#27ae60' : '#e74c3c' }}>
                    {t.transaction_type}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{t.category}</td>
                <td style={{ padding: '14px 16px', color: '#555', maxWidth: '200px' }}>{t.description || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{t.crop_name || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{t.field_name || '—'}</td>
                <td style={{ padding: '14px 16px', fontWeight: '700', color: t.transaction_type === 'income' ? '#27ae60' : '#e74c3c' }}>{fmt(t.amount)}</td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEdit(t)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                    <button onClick={() => handleDelete(t.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalRecords={filtered.length}
          rowsPerPage={10}
        />
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Transaction' : `Add ${form.transaction_type === 'income' ? 'Income' : 'Expense'}`}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={form.transaction_type} onChange={e => setForm({ ...form, transaction_type: e.target.value, category: '' })}>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Category *</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="">{catsLoaded ? '-- Select --' : 'Loading...'}</option>
                  {getCategoryOptions().map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Amount (Rs) *</label>
                <input style={inputStyle} type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Date *</label>
                <input style={inputStyle} type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Linked Field</label>
                <select style={inputStyle} value={form.land_id} onChange={e => setForm({ ...form, land_id: e.target.value })}>
                  <option value="">-- None --</option>
                  {lands.map(l => <option key={l.id} value={l.id}>{l.field_name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Linked Crop</label>
                <select style={inputStyle} value={form.crop_id} onChange={e => setForm({ ...form, crop_id: e.target.value })}>
                  <option value="">-- None --</option>
                  {crops.map(c => <option key={c.id} value={c.id}>{c.crop_name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Payment Method</label>
                <select style={inputStyle} value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Reference No</label>
                <input style={inputStyle} value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} placeholder="Optional" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Description</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Details..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceTab;